import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { Link, Navigate, useLocation, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { createAppointment, uploadAppointmentPaymentSlip } from '../../api/appointments';
import PaymentSlipSection from '../../components/payments/PaymentSlipSection';
import {
  fetchBookingStaff,
  fetchDiscoverBranches,
  fetchDiscoverSalons,
  fetchDiscoverSalon,
  fetchDiscoverServices,
} from '../../api/discover';
import BankAccountCards from '../../components/payments/BankAccountCards';
import MarketplaceLayout from '../../components/marketplace/MarketplaceLayout';
import DatePicker from '../../components/ui/DatePicker';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';
import { loginRedirectForBook } from '../../utils/bookingAuth';
import { resolveBookBranchId } from '../../utils/serviceBranches';
import { getDashboardPath } from '../../utils/rolePaths';
import { getPaymentPolicy, normalizePolicy, slipAllowsUpload } from '../../utils/paymentSlip';

const TIME_SLOTS = [];
for (let h = 9; h <= 18; h++) {
  TIME_SLOTS.push(`${String(h).padStart(2, '0')}:00`);
  if (h < 18) TIME_SLOTS.push(`${String(h).padStart(2, '0')}:30`);
}

const ClientBookPage = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { isAuthenticated, role, loading: authLoading } = useAuth();
  const { showToast } = useToast();
  const queryClient = useQueryClient();
  const [step, setStep] = useState(1);
  const [search, setSearch] = useState('');
  const [tenantId, setTenantId] = useState('');
  const [branchId, setBranchId] = useState('');
  const [serviceId, setServiceId] = useState('');
  const [preferredDate, setPreferredDate] = useState('');
  const [preferredTime, setPreferredTime] = useState('');
  const [clientNotes, setClientNotes] = useState('');
  const [preferredStaffId, setPreferredStaffId] = useState('');
  const [done, setDone] = useState(null);

  const canBook = isAuthenticated && role === 'CLIENT';

  const { data: salonsData } = useQuery({
    queryKey: ['book-salons', search],
    queryFn: () => fetchDiscoverSalons({ search: search || undefined, limit: 50 }),
    enabled: canBook && step === 1,
  });

  const { data: branchData } = useQuery({
    queryKey: ['book-branches', tenantId],
    queryFn: () => fetchDiscoverBranches(tenantId),
    enabled: canBook && step >= 2 && !!tenantId,
  });

  const { data: servicesData, isFetching: servicesLoading } = useQuery({
    queryKey: ['book-services', tenantId, branchId],
    queryFn: () => fetchDiscoverServices(tenantId, branchId),
    enabled: canBook && !!tenantId && !!branchId && step >= 3,
  });

  const { data: allServicesData } = useQuery({
    queryKey: ['book-services-all', tenantId],
    queryFn: () => fetchDiscoverServices(tenantId),
    enabled: canBook && !!tenantId && !!serviceId && !branchId,
  });

  const { data: salonDetailData } = useQuery({
    queryKey: ['book-salon-detail', tenantId],
    queryFn: () => fetchDiscoverSalon(tenantId),
    enabled: canBook && !!tenantId && step >= 4,
  });

  const { data: bookingStaffData, isFetching: bookingStaffLoading } = useQuery({
    queryKey: ['booking-staff', tenantId, serviceId, branchId, preferredDate, preferredTime],
    queryFn: () =>
      fetchBookingStaff(tenantId, serviceId, {
        branchId,
        date: preferredDate,
        time: preferredTime,
      }),
    enabled: canBook && !!tenantId && !!serviceId && !!branchId && !!preferredDate && !!preferredTime && step >= 4,
  });

  useEffect(() => {
    setPreferredStaffId('');
  }, [branchId, preferredDate, preferredTime, serviceId]);

  const bookMut = useMutation({
    mutationFn: createAppointment,
    onSuccess: (res) => {
      if (res.staffAvailabilityWarning) {
        showToast(res.staffAvailabilityWarning, 'warning');
      }
      setDone(res.appointment);
      setStep(5);
    },
    onError: (err) => showToast(err.response?.data?.message || t('toast.bookingFailed'), 'error'),
  });

  const slipUploadMut = useMutation({
    mutationFn: ({ appointmentId, file, bankAccountId }) =>
      uploadAppointmentPaymentSlip(appointmentId, file, bankAccountId),
    onSuccess: (res) => {
      showToast(t('toast.paymentSlipUploaded'), 'success');
      setDone(res.appointment);
      queryClient.invalidateQueries({ queryKey: ['client-appointments'] });
    },
    onError: (err) => showToast(err.response?.data?.message || t('errors.uploadFailed'), 'error'),
  });

  const selectedService =
    servicesData?.services?.find((s) => s._id === serviceId) ||
    allServicesData?.services?.find((s) => s._id === serviceId);
  const selectedBranch = branchData?.branches?.find((b) => b._id === branchId);
  const selectedStaff = useMemo(
    () => (bookingStaffData?.staff || []).find((s) => s._id === preferredStaffId),
    [bookingStaffData, preferredStaffId]
  );

  const bookingPrice = useMemo(() => {
    if (!selectedService) return null;
    const base = selectedService.campaignPricing
      ? Number(selectedService.campaignPricing.finalPrice || 0)
      : Number(selectedService.price || 0);
    const staffExtra = Number(selectedStaff?.serviceExtraAmount || 0);
    return { base, staffExtra, total: base + staffExtra };
  }, [selectedService, selectedStaff]);
  const selectedSalon =
    salonsData?.data?.find((s) => s._id === tenantId) || branchData?.tenant;
  const bankAccounts = salonDetailData?.salon?.bankAccounts || [];
  const showBankDetails =
    bankAccounts.length > 0 &&
    selectedService &&
    (selectedService.requiresAdvance ||
      slipAllowsUpload(selectedService.paymentSlipPolicy));

  const showPaymentOnBooked =
    done &&
    (done.payment?.requiresAdvance || slipAllowsUpload(getPaymentPolicy(done)));

  const bookedBankAccounts = done?.bankAccounts || bankAccounts;

  useEffect(() => {
    const preTenant = searchParams.get('tenantId');
    const preBranch = searchParams.get('branchId');
    const preService = searchParams.get('serviceId');
    if (preTenant) {
      setTenantId(preTenant);
      if (preBranch) setBranchId(preBranch);
      if (preService) {
        setServiceId(preService);
        setStep(preBranch ? 4 : 3);
      } else if (preBranch) {
        setStep(3);
      } else {
        setStep(2);
      }
    }
  }, [searchParams]);

  useEffect(() => {
    if (branchId || !branchData?.branches?.length) return;
    if (branchData.branches.length === 1) {
      setBranchId(branchData.branches[0]._id);
      if (step === 2) setStep(3);
    }
  }, [branchData, branchId, step]);

  useEffect(() => {
    if (branchId || !serviceId) return;
    const pool = allServicesData?.services || servicesData?.services;
    if (!pool?.length) return;
    const svc = pool.find((s) => s._id === serviceId);
    const resolved = resolveBookBranchId(svc, branchId);
    if (resolved) setBranchId(resolved);
  }, [branchId, serviceId, allServicesData, servicesData]);

  useEffect(() => {
    if (!branchId || !serviceId || step !== 3) return;
    if (searchParams.get('serviceId') === serviceId) setStep(4);
  }, [branchId, serviceId, step, searchParams]);

  const confirmBooking = () => {
    bookMut.mutate({
      branchId,
      serviceId,
      preferredDate,
      preferredTime,
      clientNotes,
      preferredStaffId: preferredStaffId || undefined,
    });
  };

  if (authLoading) {
    return (
      <MarketplaceLayout>
        <p className="p-8 text-zinc-600 dark:text-zinc-500">{t('common.loading')}</p>
      </MarketplaceLayout>
    );
  }

  if (!isAuthenticated) {
    return (
      <Navigate
        to={loginRedirectForBook(`${location.pathname}${location.search}`)}
        replace
      />
    );
  }

  if (role !== 'CLIENT') {
    return (
      <MarketplaceLayout>
        <div className="mx-auto max-w-md px-6 py-12 text-center">
          <h1 className="text-xl font-semibold">{t('appointments.clientAccountRequired')}</h1>
          <p className="mt-2 text-sm text-zinc-600 dark:text-zinc-500">
            {t('appointments.clientAccountRequiredDesc')}
          </p>
          <Link
            to={getDashboardPath(role)}
            className="mt-6 inline-block rounded-lg bg-glow-600 px-4 py-2 text-sm text-white"
          >
            {t('common.goToDashboard')}
          </Link>
        </div>
      </MarketplaceLayout>
    );
  }

  if (step === 5 && done) {
    return (
      <MarketplaceLayout>
        <div className="mx-auto max-w-md px-6 py-12 text-center">
          <p className="text-4xl">✓</p>
          <h2 className="mt-4 text-xl font-semibold">{t('appointments.bookedTitle')}</h2>
          <div className="mt-6 rounded-lg border border-zinc-200 dark:border-zinc-800 p-4 text-left text-sm">
            <p><strong>{done.serviceName}</strong></p>
            <p className="text-zinc-600 dark:text-zinc-500">{done.branchName}</p>
            <p className="mt-2">{new Date(done.preferredDate).toLocaleDateString()} at {done.preferredTime}</p>
            <p className="mt-2 text-amber-400">{t('appointments.awaitingConfirmation')}</p>
          </div>
          {showPaymentOnBooked && (
            <div className="mt-4 text-left">
              <PaymentSlipSection
                entity={done}
                requiresAdvance={done.payment?.requiresAdvance}
                advanceAmount={done.payment?.advanceAmount}
                bankAccounts={bookedBankAccounts}
                salonMode={false}
                onUpload={(file, bankAccountId) =>
                  slipUploadMut.mutate({ appointmentId: done._id, file, bankAccountId })
                }
                uploading={slipUploadMut.isPending}
              />
            </div>
          )}
          {isAuthenticated && role === 'CLIENT' ? (
            <Link
              to="/client/appointments"
              className="mt-6 inline-block w-full rounded-lg bg-glow-600 py-2.5 text-sm font-medium text-white"
            >
              {t('appointments.trackAppointment')}
            </Link>
          ) : (
            <Link to="/marketplace" className="mt-6 inline-block text-sm text-glow-400">
              {t('appointments.backToMarketplace')}
            </Link>
          )}
        </div>
      </MarketplaceLayout>
    );
  }

  return (
    <MarketplaceLayout>
      <div className="mx-auto max-w-3xl px-6 py-8">
        <h1 className="text-2xl font-semibold">{t('appointments.bookTitle')}</h1>
        <p className="mt-1 text-sm text-zinc-600 dark:text-zinc-500">{t('appointments.bookStepOf', { step })}</p>

        <div className="mb-8 mt-6 h-2 overflow-hidden rounded-full bg-zinc-100 dark:bg-zinc-800">
          <div className="h-full bg-glow-600 transition-all" style={{ width: `${(step / 4) * 100}%` }} />
        </div>

        {step === 1 && (
          <div className="space-y-4">
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t('appointments.searchSalons')}
              className="w-full rounded-lg border border-zinc-300 dark:border-zinc-700 bg-zinc-50 dark:bg-zinc-950 px-3 py-2 text-sm"
            />
            <div className="space-y-2">
              {(salonsData?.data || []).map((salon) => (
                <button
                  key={salon._id}
                  type="button"
                  onClick={() => { setTenantId(salon._id); setStep(2); }}
                  className="w-full rounded-xl border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 p-4 text-left hover:border-glow-600"
                >
                  <p className="font-medium">{salon.businessName}</p>
                  <p className="text-sm text-zinc-600 dark:text-zinc-500">{salon.address}</p>
                  <p className="text-xs text-amber-400">★ {salon.averageRating}</p>
                </button>
              ))}
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-4">
            <p className="text-sm text-zinc-600 dark:text-zinc-500">
              {t('common.salon')}: <strong>{selectedSalon?.businessName}</strong>
            </p>
            <h3 className="font-medium">{t('appointments.selectBranch')}</h3>
            {(branchData?.branches || []).map((branch) => (
              <button
                key={branch._id}
                type="button"
                onClick={() => { setBranchId(branch._id); setServiceId(''); setStep(3); }}
                className="w-full rounded-xl border border-zinc-200 dark:border-zinc-800 p-4 text-left hover:border-glow-600"
              >
                <p className="font-medium">
                  {branch.name} {branch.isMain && `· ${t('common.mainBranch')}`}
                </p>
                <p className="text-sm text-zinc-600 dark:text-zinc-500">{branch.address}</p>
              </button>
            ))}
            <button type="button" onClick={() => setStep(1)} className="text-sm text-zinc-600 dark:text-zinc-400">← {t('common.back')}</button>
          </div>
        )}

        {step === 3 && (
          <div className="space-y-4">
            <button type="button" onClick={() => { setServiceId(''); setStep(2); }} className="text-sm text-zinc-600 dark:text-zinc-400">← {t('common.back')}</button>
            <p className="text-sm text-zinc-600 dark:text-zinc-500">
              {t('appointments.selectBranch')}: <strong>{selectedBranch?.name}</strong>
            </p>
            {servicesLoading && <p className="text-sm text-zinc-500">{t('common.loading')}</p>}
            {!servicesLoading && !(servicesData?.services || []).length && (
              <p className="text-sm text-zinc-500">{t('salon.services.noServicesAtBranch')}</p>
            )}
            <div className="grid gap-4 sm:grid-cols-2">
              {(servicesData?.services || []).map((service) => (
                <button
                  key={service._id}
                  type="button"
                  onClick={() => { setServiceId(service._id); setStep(4); }}
                  className={`rounded-xl border p-4 text-left ${serviceId === service._id ? 'border-glow-500' : 'border-zinc-200 dark:border-zinc-800'}`}
                >
                  {service.imageUrl && <img src={service.imageUrl} alt="" className="mb-3 h-24 w-full rounded-lg object-cover" />}
                  <p className="font-medium">{service.name}</p>
                  {service.campaignPricing ? (
                    <div className="mt-1">
                      <p className="text-xs text-emerald-600 dark:text-emerald-400">
                        {service.campaignPricing.discountMode === 'PERCENTAGE'
                          ? t('marketplace.percentOff', { value: service.campaignPricing.discountValue })
                          : t('marketplace.flatOff', { value: Number(service.campaignPricing.discountValue || 0).toLocaleString() })}
                      </p>
                      <p className="text-sm text-zinc-600 dark:text-zinc-500">
                        {t('common.duration', { minutes: service.duration })} ·{' '}
                        <span className="font-medium text-glow-300">
                          {t('common.currency', { amount: Number(service.campaignPricing.finalPrice || 0).toLocaleString() })}
                        </span>
                        <span className="ml-2 text-zinc-500 line-through">
                          {t('common.currency', { amount: Number(service.campaignPricing.basePrice || 0).toLocaleString() })}
                        </span>
                      </p>
                    </div>
                  ) : (
                    <p className="text-sm text-zinc-600 dark:text-zinc-500">
                      {t('common.duration', { minutes: service.duration })} · {t('common.currency', { amount: service.price?.toLocaleString() })}
                    </p>
                  )}
                </button>
              ))}
            </div>
          </div>
        )}

        {step === 4 && (
          <div className="space-y-6">
            <button type="button" onClick={() => setStep(3)} className="text-sm text-zinc-600 dark:text-zinc-400">← {t('common.back')}</button>
            <DatePicker
              id="preferred-date"
              label={t('appointments.preferredDate')}
              required
              min={new Date().toISOString().split('T')[0]}
              value={preferredDate}
              onChange={(e) => setPreferredDate(e.target.value)}
            />
            <div>
              <label className="mb-2 block text-sm text-zinc-600 dark:text-zinc-400">{t('appointments.preferredTime')}</label>
              <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 md:grid-cols-4">
                {TIME_SLOTS.map((slot) => (
                  <button
                    key={slot}
                    type="button"
                    onClick={() => setPreferredTime(slot)}
                    className={`rounded-lg border py-2 text-sm ${preferredTime === slot ? 'border-glow-500 bg-glow-600/20' : 'border-zinc-300 dark:border-zinc-700'}`}
                  >
                    {slot}
                  </button>
                ))}
              </div>
            </div>
            {preferredDate && preferredTime && (
              <div>
                <label className="mb-2 block text-sm text-zinc-600 dark:text-zinc-400">
                  {t('appointments.preferredStaffOptional')}
                </label>
                {bookingStaffLoading ? (
                  <p className="text-sm text-zinc-500">{t('common.loading')}</p>
                ) : (bookingStaffData?.staff || []).length > 0 ? (
                  <>
                    <select
                      value={preferredStaffId}
                      onChange={(e) => setPreferredStaffId(e.target.value)}
                      className="w-full rounded-lg border border-zinc-300 dark:border-zinc-700 bg-zinc-50 dark:bg-zinc-950 px-3 py-2 text-sm"
                    >
                      <option value="">{t('appointments.noStaffPreference')}</option>
                      {(bookingStaffData?.staff || []).map((s) => (
                        <option key={s._id} value={s._id}>
                          {s.firstName} {s.lastName}
                          {s.serviceExtraAmount > 0
                            ? ` (+${Number(s.serviceExtraAmount).toLocaleString()})`
                            : ''}
                        </option>
                      ))}
                    </select>
                    <p className="mt-1 text-xs text-zinc-500">{t('appointments.preferredStaffHint')}</p>
                  </>
                ) : (
                  <p className="text-sm text-zinc-500">{t('appointments.noStaffAtSlot')}</p>
                )}
              </div>
            )}
            <div>
              <label className="mb-2 block text-sm text-zinc-600 dark:text-zinc-400">{t('common.notesOptional')}</label>
              <textarea value={clientNotes} onChange={(e) => setClientNotes(e.target.value)} rows={3} className="w-full rounded-lg border border-zinc-300 dark:border-zinc-700 bg-zinc-50 dark:bg-zinc-950 px-3 py-2 text-sm" />
            </div>
            <div className="rounded-xl border border-zinc-200 dark:border-zinc-800 p-4 text-sm">
              <h3 className="font-medium">{t('appointments.review')}</h3>
              <p className="mt-2">{selectedService?.name} at {selectedBranch?.name}</p>
              <p className="text-zinc-600 dark:text-zinc-500">{preferredDate} · {preferredTime}</p>
              {selectedStaff && (
                <p className="text-zinc-600 dark:text-zinc-500">
                  {t('appointments.preferredStaff')}: {selectedStaff.firstName} {selectedStaff.lastName}
                </p>
              )}
              {bookingPrice && (
                <div className="mt-2 space-y-1">
                  {selectedService?.campaignPricing ? (
                    <p>
                      <span className="text-zinc-500 line-through">
                        {t('common.currency', { amount: Number(selectedService.campaignPricing.basePrice || 0).toLocaleString() })}
                      </span>
                      <span className="ml-2">{t('common.currency', { amount: bookingPrice.base.toLocaleString() })}</span>
                    </p>
                  ) : (
                    <p>{t('pricing.basePrice')}: {t('common.currency', { amount: bookingPrice.base.toLocaleString() })}</p>
                  )}
                  {bookingPrice.staffExtra > 0 && selectedStaff && (
                    <p className="text-amber-600 dark:text-amber-400">
                      {t('pricing.staffPremiumNamed', { name: `${selectedStaff.firstName} ${selectedStaff.lastName}` })}
                      : +{t('common.currency', { amount: bookingPrice.staffExtra.toLocaleString() })}
                    </p>
                  )}
                  <p className="font-semibold text-glow-300">
                    {t('pricing.finalPrice')}: {t('common.currency', { amount: bookingPrice.total.toLocaleString() })}
                  </p>
                </div>
              )}
              {selectedService?.requiresAdvance && Number(selectedService.advanceAmount) > 0 && (
                <p className="mt-2 text-amber-300">
                  {t('payments.advanceDue', { amount: Number(selectedService.advanceAmount).toLocaleString() })}
                </p>
              )}
              {selectedService && slipAllowsUpload(selectedService.paymentSlipPolicy) && (
                <p className="mt-2 text-zinc-500">
                  {t(`payments.policy.${normalizePolicy(selectedService.paymentSlipPolicy).toLowerCase()}`)}
                </p>
              )}
              {showBankDetails && (
                <div className="mt-4">
                  <BankAccountCards accounts={bankAccounts} />
                </div>
              )}
              {selectedService &&
                (selectedService.requiresAdvance || slipAllowsUpload(selectedService.paymentSlipPolicy)) && (
                <p className="mt-3 text-xs text-zinc-500">{t('appointments.uploadSlipAfterConfirm')}</p>
              )}
            </div>
            <button
              type="button"
              disabled={!preferredDate || !preferredTime || bookMut.isPending}
              onClick={confirmBooking}
              className="w-full rounded-lg bg-glow-600 py-2.5 text-sm font-medium text-white disabled:opacity-50"
            >
              {bookMut.isPending ? t('appointments.booking') : t('appointments.confirmBooking')}
            </button>
          </div>
        )}
      </div>
    </MarketplaceLayout>
  );
};

export default ClientBookPage;
